/**
 * "What is this agent actually doing?" — the one line an agent card or window header shows.
 *
 * Agents are the part of Morrow that works unattended, and the UI never said so: a card showed
 * name, role, autonomy and `status`, which answers "is it switched on" but not "did it do
 * anything". An owner watching a scheduled agent had no way to tell a working agent from an idle
 * one, or to see that last night's wake failed. Status alone is not activity.
 *
 * Pure on purpose — no clock reads, no fetching — so the exact wording is unit-testable and both
 * surfaces render the same sentence from the same inputs.
 */

/** The run fields this module needs; a structural subset of the client's `AgentRun`. */
export interface AgentActivityRun {
  readonly agentId: string;
  readonly status: string;
  readonly source: string;
  readonly createdAt: string;
}

/** The agent fields this module needs; a structural subset of the client's `Agent`. */
export interface AgentActivityAgent {
  readonly id: string;
  readonly status: string;
  /** Optional because the API omits it entirely for an agent that never self-schedules. */
  readonly nextRunAt?: string | null;
}

/** A run that has opened but not settled — the agent is working right now. */
const RUNNING_STATUSES: ReadonlySet<string> = new Set(["queued", "started"]);

/**
 * Newest run per agent, keyed by agent id. The runs endpoint returns every agent's runs newest
 * first; this keeps the first one seen for each agent rather than re-sorting, so it stays O(n)
 * and does not depend on timestamp parsing.
 */
export function newestRunByAgent<Run extends AgentActivityRun>(
  runs: ReadonlyArray<Run>,
): ReadonlyMap<string, Run> {
  const newest = new Map<string, Run>();
  for (const run of runs) {
    if (!newest.has(run.agentId)) newest.set(run.agentId, run);
  }
  return newest;
}

/** Round a millisecond gap into the coarsest unit that still reads naturally. */
function humanizeGap(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return "under a minute";
  if (minutes === 1) return "1 minute";
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  if (hours === 1) return "1 hour";
  if (hours < 24) return `${hours} hours`;
  const days = Math.round(hours / 24);
  return days === 1 ? "1 day" : `${days} days`;
}

/** How the line should be drawn, so each surface can style it without re-deriving meaning. */
export type AgentActivityTone = "working" | "scheduled" | "problem" | "idle";

export interface AgentActivity {
  readonly tone: AgentActivityTone;
  readonly label: string;
}

/**
 * The single activity line for one agent.
 *
 * Priority is deliberate: what it is doing NOW beats what it will do next, which beats what it
 * last did. A paused agent says so and nothing else — a "next wake in 5 minutes" on a paused
 * agent is exactly the contradiction that made people think pause did nothing.
 *
 * `nowMs` is passed in rather than read from the clock so this stays pure.
 */
export function agentActivityLine(input: {
  readonly agent: AgentActivityAgent;
  readonly newestRun: AgentActivityRun | undefined;
  readonly nowMs: number;
}): AgentActivity {
  const { agent, newestRun, nowMs } = input;

  if (agent.status === "archived") return { tone: "idle", label: "Archived" };
  if (agent.status === "paused") {
    return {
      tone: "idle",
      label:
        newestRun && RUNNING_STATUSES.has(newestRun.status)
          ? "Paused — finishing what it started"
          : "Paused — won't wake on its own",
    };
  }

  if (newestRun && RUNNING_STATUSES.has(newestRun.status)) {
    return { tone: "working", label: "Working now" };
  }

  if (agent.nextRunAt) {
    const nextMs = Date.parse(agent.nextRunAt);
    if (Number.isFinite(nextMs)) {
      return nextMs <= nowMs
        ? { tone: "scheduled", label: "Waking up now" }
        : { tone: "scheduled", label: `Next wake in ${humanizeGap(nextMs - nowMs)}` };
    }
  }

  if (newestRun) {
    const ranMs = Date.parse(newestRun.createdAt);
    const ago = Number.isFinite(ranMs) ? ` ${humanizeGap(nowMs - ranMs)} ago` : "";
    switch (newestRun.status) {
      case "failed":
        return { tone: "problem", label: `Last run failed${ago}` };
      case "skipped":
        return { tone: "problem", label: `Last wake skipped${ago}` };
      case "stopped":
        return { tone: "idle", label: `Stopped${ago}` };
      default:
        return { tone: "idle", label: `Last ran${ago}` };
    }
  }

  return { tone: "idle", label: "Hasn't run yet" };
}
